/**
 * GeometryPoint - Renders point geometry in Three.js
 *
 * All points look the same (circles) - Rhino-style unified appearance
 * Supports dragging for moving geometry
 */

import React, { useMemo, useRef, useState, useCallback } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { GEOMETRY_COLORS, SIZES } from '../../utils/constants';

const Z_POINT = 8; // Z-level for geometry points (above curves)
const HIT_RADIUS = 300; // Hit area radius in mm (30cm for easier clicking)

/**
 * Render a single geometry point with drag support
 */
export function GeometryPoint({
  point,
  isSelected = false,
  isHovered = false,
  onClick,
  onPointerOver,
  onPointerOut,
  onDragStart,
  onDrag,
  onDragEnd,
  draggable = true,
  pointRadius: customRadius,
}) {
  const meshRef = useRef();
  const [isDragging, setIsDragging] = useState(false);
  const { camera, gl } = useThree();

  // Color based on state - ALL points look the same (unified Rhino-style)
  const color = useMemo(() => {
    if (isSelected) return GEOMETRY_COLORS.pointSelected;
    if (isHovered) return GEOMETRY_COLORS.pointHover;
    return GEOMETRY_COLORS.point;
  }, [isSelected, isHovered]);

  // Visual radius - use custom or default, larger when selected
  const baseRadius = customRadius || SIZES.pointRadius;
  const visualRadius = isSelected ? baseRadius * 1.3 : baseRadius;
  // Hit radius - always large for easy clicking
  const hitRadius = Math.max(HIT_RADIUS, visualRadius * 1.5);

  // Mouse position to world coordinates
  const getWorldPosition = useCallback((event) => {
    const rect = gl.domElement.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const vector = new THREE.Vector3(x, y, 0);
    vector.unproject(camera);

    return { x: vector.x, y: vector.y };
  }, [camera, gl]);

  const handlePointerDown = useCallback((e) => {
    if (!draggable) return;

    e.stopPropagation();
    setIsDragging(true);
    onDragStart?.(point.id);
    document.body.style.cursor = 'grabbing';

    let lastDragPos = { x: point.x, y: point.y };

    const handlePointerMove = (moveEvent) => {
      const pos = getWorldPosition(moveEvent);
      lastDragPos = { x: pos.x, y: pos.y };
      onDrag?.(point.id, pos.x, pos.y);
    };

    const handlePointerUp = () => {
      setIsDragging(false);
      onDragEnd?.(point.id, lastDragPos.x, lastDragPos.y);
      document.body.style.cursor = 'default';
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  }, [draggable, point?.id, point?.x, point?.y, getWorldPosition, onDragStart, onDrag, onDragEnd]);

  const handlePointerOver = useCallback((e) => {
    e.stopPropagation();
    onPointerOver?.(e);
    document.body.style.cursor = draggable ? 'grab' : 'pointer';
  }, [onPointerOver, draggable]);

  const handlePointerOut = useCallback((e) => {
    if (!isDragging) {
      onPointerOut?.(e);
      document.body.style.cursor = 'default';
    }
  }, [isDragging, onPointerOut]);

  if (!point) return null;

  // ALL points: Circles (unified appearance)
  return (
    <group position={[point.x, point.y, Z_POINT]}>
      {/* Invisible hit area for easier clicking */}
      <mesh
        position={[0, 0, -1]}
        onClick={onClick}
        onPointerDown={handlePointerDown}
        onPointerOver={handlePointerOver}
        onPointerOut={handlePointerOut}
      >
        <circleGeometry args={[hitRadius, 16]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Visible point */}
      <mesh ref={meshRef}>
        <circleGeometry args={[visualRadius, 16]} />
        <meshBasicMaterial color={color} transparent opacity={0.9} />
      </mesh>

      {/* Selection ring */}
      {isSelected && (
        <mesh position={[0, 0, -0.5]}>
          <ringGeometry args={[visualRadius * 1.2, visualRadius * 1.5, 16]} />
          <meshBasicMaterial color={GEOMETRY_COLORS.selected} transparent opacity={0.5} />
        </mesh>
      )}
    </group>
  );
}

/**
 * Render all geometry points
 */
export function GeometryPoints({
  points = [],
  selectedIds = [],
  hoveredId = null,
  onPointClick,
  onPointHover,
  onPointDragStart,
  onPointDrag,
  onPointDragEnd,
  draggable = true,
  pointRadius,
}) {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <group name="geometryPoints">
      {points.map(point => (
        <GeometryPoint
          key={point.id}
          point={point}
          isSelected={selectedSet.has(point.id)}
          isHovered={hoveredId === point.id}
          onClick={(e) => {
            e.stopPropagation();
            onPointClick?.(point.id, e);
          }}
          onPointerOver={(e) => {
            e.stopPropagation();
            onPointHover?.(point.id);
          }}
          onPointerOut={() => {
            onPointHover?.(null);
          }}
          onDragStart={onPointDragStart}
          onDrag={onPointDrag}
          onDragEnd={onPointDragEnd}
          draggable={draggable}
          pointRadius={pointRadius}
        />
      ))}
    </group>
  );
}

export default GeometryPoint;
